import { useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { PackageSearch } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import { PLANNED_ORDER_STATUS_LABELS, fmtQty } from '@/features/manufacturing/planning/engine';
import type { ItemPlan, PlannedOrderStatus, PlanningModel } from '@/features/manufacturing/planning/types';
import { formatCurrency } from '@/lib/formatters';
import { formatQtyUom } from '@/lib/uom';
import { cn } from '@/lib/utils';
import { ItemPlanSheet } from './ItemPlanSheet';
import { OrderStatusBadge, orderQty, qty, shortDate } from './shared';

interface PartsTabProps {
  model: PlanningModel;
}

type RowStatus = PlannedOrderStatus | 'covered' | 'no_demand';

const ROW_STATUS_LABELS: Record<RowStatus, string> = {
  ...PLANNED_ORDER_STATUS_LABELS,
  covered: 'Covered',
  no_demand: 'Not needed',
};

function rowStatus(plan: ItemPlan): RowStatus {
  if (plan.orders.length > 0) return plan.orders[0].status;
  if (plan.grossRequirement <= 0) return 'no_demand';
  return 'covered';
}

/**
 * Every purchased part the plan touches, most urgent first. The row is
 * the headline — "runs out X, order by Y" — and the drawer behind it
 * holds the day-by-day projection, the lead-time evidence and the
 * open POs that feed the numbers.
 */
export function PartsTab({ model }: PartsTabProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = selectedId ? (model.itemsById.get(selectedId) ?? null) : null;

  const columns = useMemo<ColumnDef<ItemPlan, unknown>[]>(
    () => [
      {
        id: 'part',
        accessorFn: (r) => `${r.item.sku} ${r.item.name}`,
        header: 'Part',
        size: 240,
        minSize: 160,
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate font-medium">{row.original.item.name}</div>
            <div className="truncate font-mono text-[11px] text-muted-foreground">
              {row.original.item.sku}
              {row.original.usedIn.length > 0 && (
                <span className="ml-1.5 font-sans">
                  · in {row.original.usedIn.map((u) => model.itemsById.get(u.itemId)?.item.name ?? '').filter(Boolean).slice(0, 2).join(', ')}
                  {row.original.usedIn.length > 2 ? ` +${row.original.usedIn.length - 2}` : ''}
                </span>
              )}
            </div>
          </div>
        ),
      },
      {
        id: 'status',
        accessorFn: (r) => rowStatus(r),
        header: 'Status',
        size: 150,
        minSize: 120,
        filterFn: arrIncludesSome,
        cell: ({ row }) => {
          const status = rowStatus(row.original);
          if (status === 'covered') return <Badge variant="success-soft">Covered</Badge>;
          if (status === 'no_demand') return <Badge variant="muted">Not needed</Badge>;
          return <OrderStatusBadge status={status} />;
        },
      },
      {
        id: 'runout',
        accessorFn: (r) => r.runoutDate ?? '9999-12-31',
        header: 'Runs out',
        size: 130,
        minSize: 110,
        cell: ({ row }) => {
          const plan = row.original;
          if (!plan.runoutDate) {
            return (
              <span className="text-xs text-muted-foreground">
                {plan.grossRequirement > 0 ? `not in ${model.settings.horizonDays}d` : '—'}
              </span>
            );
          }
          const shortage = plan.shortages[0];
          return (
            <div className="whitespace-nowrap">
              <span className={cn('tabular-nums', shortage ? 'font-medium text-fresh-salmon-dark' : 'text-foreground')}>
                {shortDate(plan.runoutDate)}
              </span>
              {shortage && (
                <div className="text-[11px] text-fresh-salmon-dark">
                  {shortage.workingDays}d short, {fmtQty(shortage.maxShortQty)} units
                </div>
              )}
            </div>
          );
        },
      },
      {
        id: 'order_by',
        accessorFn: (r) => r.orders[0]?.idealOrderDate ?? '9999-12-31',
        header: 'Order by',
        size: 160,
        minSize: 130,
        cell: ({ row }) => {
          const order = row.original.orders[0];
          if (!order) return <span className="text-muted-foreground">—</span>;
          if (!order.idealOrderDate) {
            return <span className="text-xs text-fresh-salmon-dark">needs lead time</span>;
          }
          const late = order.status === 'late' || order.status === 'order_now';
          return (
            <div className="whitespace-nowrap">
              <span className={cn('tabular-nums', late && 'font-medium text-fresh-salmon-dark')}>
                {late ? 'Today' : shortDate(order.idealOrderDate)}
              </span>
              <div className="text-[11px] text-muted-foreground">
                {order.status === 'late' ? `was ${shortDate(order.latestOrderDate)}` : `latest ${shortDate(order.latestOrderDate)}`}
              </div>
            </div>
          );
        },
      },
      {
        id: 'suggested',
        accessorFn: (r) => r.orders[0]?.quantity ?? 0,
        header: () => <span className="block text-right">Suggested order</span>,
        size: 170,
        minSize: 130,
        cell: ({ row }) => {
          const order = row.original.orders[0];
          if (!order) return <span className="block text-right text-muted-foreground">—</span>;
          return (
            <div className="text-right">
              {order.supplier?.purchaseUom && order.supplier.unitsPerPurchaseUom !== 1 ? (
                <>
                  <span className="tabular-nums font-medium">
                    {formatQtyUom(order.purchaseQuantity, order.supplier.purchaseUom)}
                  </span>
                  <div className="text-[11px] tabular-nums text-muted-foreground">
                    = {qty(order.quantity, row.original.item.baseUom)}
                  </div>
                </>
              ) : (
                <>
                  <span className="tabular-nums font-medium">{fmtQty(order.quantity)}</span>
                  <span className="ml-1 text-xs text-muted-foreground">{row.original.item.baseUom !== 'each' ? row.original.item.baseUom : ''}</span>
                </>
              )}
              <div className="flex flex-wrap justify-end gap-1 text-[11px] text-muted-foreground">
                {order.moqApplied && <span>MOQ</span>}
                {order.multipleApplied && <span>pack</span>}
                {order.priceBreakOption && (
                  <span className={order.priceBreakOption.recommended ? 'text-bright-moss-dark' : ''}>
                    break @{' '}
                    {order.supplier?.purchaseUom && order.supplier.unitsPerPurchaseUom !== 1
                      ? formatQtyUom(order.priceBreakOption.purchaseQuantity, order.supplier.purchaseUom)
                      : fmtQty(order.priceBreakOption.quantity)}
                  </span>
                )}
                {row.original.orders.length > 1 && <span>+{row.original.orders.length - 1} later</span>}
              </div>
            </div>
          );
        },
      },
      {
        id: 'cost',
        accessorFn: (r) => r.orders.reduce((acc, o) => acc + (o.totalCents ?? 0), 0),
        header: () => <span className="block text-right">Cost</span>,
        size: 110,
        minSize: 90,
        cell: ({ row }) => {
          const total = row.original.orders.reduce((acc, o) => acc + (o.totalCents ?? 0), 0);
          const missing = row.original.orders.some((o) => o.totalCents == null);
          if (row.original.orders.length === 0) return <span className="block text-right text-muted-foreground">—</span>;
          return (
            <span className="block text-right tabular-nums">
              {missing && total === 0 ? <span className="text-xs text-fresh-salmon-dark">no cost</span> : formatCurrency(total)}
            </span>
          );
        },
      },
      {
        id: 'on_hand',
        accessorFn: (r) => r.onHand,
        header: () => <span className="block text-right">On hand</span>,
        size: 110,
        minSize: 90,
        cell: ({ row }) => (
          <div className="text-right tabular-nums">
            {fmtQty(row.original.onHand)}
            {row.original.daysOfCover != null && (
              <div className="text-[11px] text-muted-foreground">{Math.floor(row.original.daysOfCover)}d cover</div>
            )}
          </div>
        ),
      },
      {
        id: 'on_order',
        accessorFn: (r) => r.onOrderPlaced + r.onOrderDraft,
        header: () => <span className="block text-right">On order</span>,
        size: 110,
        minSize: 90,
        cell: ({ row }) => {
          const { onOrderPlaced, onOrderDraft } = row.original;
          if (!onOrderPlaced && !onOrderDraft) return <span className="block text-right text-muted-foreground">—</span>;
          return (
            <div className="text-right tabular-nums">
              {fmtQty(onOrderPlaced)}
              {onOrderDraft > 0 && <div className="text-[11px] text-muted-foreground">+{fmtQty(onOrderDraft)} draft</div>}
            </div>
          );
        },
      },
      {
        id: 'need',
        accessorFn: (r) => r.grossRequirement,
        header: () => <span className="block text-right">Need</span>,
        size: 110,
        minSize: 90,
        cell: ({ row }) => (
          <div className="text-right tabular-nums">
            {fmtQty(row.original.grossRequirement)}
            {row.original.avgDailyUsage > 0 && (
              <div className="text-[11px] text-muted-foreground">{fmtQty(row.original.avgDailyUsage)}/day</div>
            )}
          </div>
        ),
      },
      {
        id: 'lead_time',
        accessorFn: (r) => r.leadTime.typicalDays ?? 9999,
        header: 'Lead time',
        size: 130,
        minSize: 100,
        cell: ({ row }) => {
          const lt = row.original.leadTime;
          if (lt.typicalDays == null) return <span className="text-xs text-fresh-salmon-dark">missing</span>;
          return (
            <div className="whitespace-nowrap tabular-nums">
              {lt.typicalDays}d
              {lt.worstDays != null && lt.worstDays !== lt.typicalDays && (
                <span className="text-muted-foreground"> – {lt.worstDays}d</span>
              )}
              <div className="text-[11px] text-muted-foreground">
                {lt.source === 'observed'
                  ? `${lt.observedSamples} receipt${lt.observedSamples === 1 ? '' : 's'}`
                  : lt.source === 'quoted'
                    ? 'vendor quote'
                    : 'catalog'}
              </div>
            </div>
          );
        },
      },
      {
        id: 'vendor',
        accessorFn: (r) => r.supplier?.vendorName ?? 'No supplier',
        header: 'Vendor',
        size: 150,
        minSize: 110,
        filterFn: arrIncludesSome,
        cell: ({ row }) =>
          row.original.supplier ? (
            <span className="truncate">{row.original.supplier.vendorName}</span>
          ) : (
            <span className="text-xs text-fresh-salmon-dark">No supplier</span>
          ),
      },
    ],
    [model],
  );

  const facetedFilters = useMemo<FacetedFilter[]>(
    () => [
      {
        columnId: 'status',
        title: 'Status',
        options: (Object.keys(ROW_STATUS_LABELS) as RowStatus[]).map((s) => ({ value: s, label: ROW_STATUS_LABELS[s] })),
      },
      { columnId: 'vendor', title: 'Vendor' },
    ],
    [],
  );

  return (
    <>
      <Card>
        <CardContent className="pt-6">
          <DataTable
            columns={columns}
            data={model.purchased}
            getRowId={(row) => row.item.id}
            onRowClick={(row) => setSelectedId(row.item.id)}
            rowClassName={(row) =>
              row.shortages.length > 0 ? 'bg-fresh-salmon/5' : row.orders[0]?.status === 'order_now' ? 'bg-fresh-salmon/[0.03]' : undefined
            }
            searchPlaceholder="Search parts, SKUs, vendors…"
            facetedFilters={facetedFilters}
            initialPageSize={50}
            pageSizes={[25, 50, 100, 200]}
            tableId="production-planning-parts"
            enableColumnResizing
            mobileCard={{
              title: (row) => row.item.name,
              subtitle: (row) => <span className="font-mono text-[11px]">{row.item.sku}</span>,
              badges: (row) => {
                const status = rowStatus(row);
                return [
                  status === 'covered' ? (
                    <Badge key="s" variant="success-soft">
                      Covered
                    </Badge>
                  ) : status === 'no_demand' ? (
                    <Badge key="s" variant="muted">
                      Not needed
                    </Badge>
                  ) : (
                    <OrderStatusBadge key="s" status={status} />
                  ),
                ];
              },
              meta: (row) => [
                { label: 'Runs out', value: shortDate(row.runoutDate) },
                {
                  label: 'Order by',
                  value: row.orders[0]
                    ? row.orders[0].status === 'late' || row.orders[0].status === 'order_now'
                      ? 'Today'
                      : shortDate(row.orders[0].idealOrderDate)
                    : '—',
                },
                { label: 'On hand', value: fmtQty(row.onHand) },
                { label: 'Suggest', value: row.orders[0] ? orderQty(row.orders[0], row.item.baseUom) : '—' },
              ],
            }}
            emptyState={
              <EmptyState
                icon={PackageSearch}
                title={model.schedules.length === 0 ? 'Add a production target to see parts' : 'No purchased parts in these BOMs'}
              />
            }
          />
        </CardContent>
      </Card>

      <ItemPlanSheet plan={selected} model={model} onOpenChange={(open) => !open && setSelectedId(null)} />
    </>
  );
}
